import { Connector } from '../../../api/connector'
import {
  RoutesBreakdown,
  MostRecentRoutesBreakdown
} from '../../../components/routes-coverage/chart'

export interface RouteCoverageSnapshotQueryArgs {
  readonly organizationUuid: string
  readonly applicationUuid: string
  readonly startDate: Date
  readonly endDate: Date
}

interface RouteCoverageTrend {
  readonly current: number
  readonly sinceYesterday: number
  readonly sinceLastWeek: number
}

interface RouteCoverageTrendItemApi {
  readonly discovered: RouteCoverageTrend
  readonly exercised: RouteCoverageTrend
  readonly vulnerable: RouteCoverageTrend
  readonly timestamp: number
}

interface MostRecentRoutesApi {
  readonly discovered: number
  readonly exercised: number
  readonly vulnerable: number
}

export interface RouteCoverageSnapshotApiResponse {
  readonly messages?: string[]
  readonly mostRecentRoutes: MostRecentRoutesApi
  readonly routeCoverageTrendItem: RouteCoverageTrendItemApi[]
}

export interface RouteCoverageSnapshot {
  readonly timeline: number[]
  readonly routesBreakdown: RoutesBreakdown
  readonly mostRecentRoutesBreakdown: MostRecentRoutesBreakdown
}

function normalizeRouteCoverageSnapshot({
  mostRecentRoutes,
  routeCoverageTrendItem
}: RouteCoverageSnapshotApiResponse): RouteCoverageSnapshot {
  const timeline = routeCoverageTrendItem.map((item) => item.timestamp)

  const routesBreakdown: RoutesBreakdown = routeCoverageTrendItem.reduce(
    (prev, curr) => {
      return {
        DISCOVERED: {
          CURRENT: [...prev.DISCOVERED.CURRENT, curr.discovered.current],
          SINCE_YESTERDAY: [
            ...prev.DISCOVERED.SINCE_YESTERDAY,
            curr.discovered.sinceYesterday
          ],
          SINCE_LAST_WEEK: [
            ...prev.DISCOVERED.SINCE_LAST_WEEK,
            curr.discovered.sinceLastWeek
          ]
        },
        EXERCISED: {
          CURRENT: [...prev.EXERCISED.CURRENT, curr.exercised.current],
          SINCE_YESTERDAY: [
            ...prev.EXERCISED.SINCE_YESTERDAY,
            curr.exercised.sinceYesterday
          ],
          SINCE_LAST_WEEK: [
            ...prev.EXERCISED.SINCE_LAST_WEEK,
            curr.exercised.sinceLastWeek
          ]
        },
        VULNERABLE: {
          CURRENT: [...prev.VULNERABLE.CURRENT, curr.vulnerable.current],
          SINCE_YESTERDAY: [
            ...prev.VULNERABLE.SINCE_YESTERDAY,
            curr.vulnerable.sinceYesterday
          ],
          SINCE_LAST_WEEK: [
            ...prev.VULNERABLE.SINCE_LAST_WEEK,
            curr.vulnerable.sinceLastWeek
          ]
        },
        NOT_EXERCISED: {
          CURRENT: [
            ...prev.NOT_EXERCISED.CURRENT,
            curr.discovered.current - curr.exercised.current
          ],
          SINCE_YESTERDAY: [],
          SINCE_LAST_WEEK: []
        }
      }
    },
    {
      DISCOVERED: {
        CURRENT: [],
        SINCE_YESTERDAY: [],
        SINCE_LAST_WEEK: []
      },
      EXERCISED: {
        CURRENT: [],
        SINCE_YESTERDAY: [],
        SINCE_LAST_WEEK: []
      },
      VULNERABLE: {
        CURRENT: [],
        SINCE_YESTERDAY: [],
        SINCE_LAST_WEEK: []
      },
      NOT_EXERCISED: {
        CURRENT: [],
        SINCE_YESTERDAY: [],
        SINCE_LAST_WEEK: []
      }
    }
  )

  const mostRecentRoutesBreakdown = {
    DISCOVERED: mostRecentRoutes.discovered,
    EXERCISED: mostRecentRoutes.exercised,
    VULNERABLE: mostRecentRoutes.vulnerable,
    NOT_EXERCISED: 0
  }

  return {
    timeline,
    routesBreakdown,
    mostRecentRoutesBreakdown
  }
}

export async function getRouteCoverageSnapshots(
  this: { readonly connector: Connector },
  {
    organizationUuid,
    applicationUuid,
    startDate,
    endDate
  }: RouteCoverageSnapshotQueryArgs
): Promise<RouteCoverageSnapshot> {
  const snapshot: RouteCoverageSnapshotApiResponse = await this.connector.get(
    `/${organizationUuid}/applications/${applicationUuid}/route/snapshots?expand=skip_links&startDate=${startDate.getTime()}&endDate=${endDate.getTime()}`
  )

  return normalizeRouteCoverageSnapshot(snapshot)
}
